import { Dispatch, SetStateAction, useContext } from 'react';
import clsx from 'clsx';

import { Icon, IconButton } from '@atom/mui';
import { EditIcon } from '@atom/mui/icons';
import { Replenishment } from '@atom/types/assetRequest';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import {
  getDisplayDateTime,
  setDisplayDate,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ViewRequestContext from '../ViewRequestContext';

import classes from './sddotView.module.css';

interface Props {
  replenishment: Replenishment;
  editId: string;
  setEditId: Dispatch<SetStateAction<string>>;
}

const ReplenishmentView = ({ replenishment, editId, setEditId }: Props) => {
  const { activeRequest } = useContext(ViewRequestContext);

  const createdByText = `Created by ${replenishment?.createdBy?.firstName} ${
    replenishment?.createdBy?.lastName
  } on ${getDisplayDateTime(replenishment?.createdDate)}`;

  const updatedByText = `Last edited by ${
    replenishment?.updatedBy?.firstName
  } ${replenishment?.updatedBy?.lastName} on ${getDisplayDateTime(
    replenishment?.updatedDate,
  )}`;

  const showUpdatedByText =
    replenishment?.createdDate !== replenishment?.updatedDate &&
    !isNilOrEmpty(replenishment?.updatedDate);

  const showEditButton =
    isNilOrEmpty(editId) && hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  return (
    <div className={classes.replenishmentForm}>
      <div className={classes.replenishmentTitle}>
        <div>
          {replenishment?.name}
          {showUpdatedByText ? (
            <span className={classes.editedSubtext}>{`(edited)`}</span>
          ) : (
            <div />
          )}
        </div>
        {showEditButton && (
          <IconButton size="small" onClick={() => setEditId(replenishment?.id)}>
            <Icon icon={EditIcon} />
          </IconButton>
        )}
      </div>
      <div className={classes.replenishmentRow}>
        <div className={clsx(classes.replenishmentCell, classes.long)}>
          <div className={classes.rowTitle}>Invoice Number</div>
          <div>{replenishment?.invoiceNumber}</div>
        </div>
        <div className={clsx(classes.replenishmentCell, classes.long)}>
          <div className={classes.rowTitle}>Date Received</div>
          <div>{setDisplayDate(replenishment?.dateReceived)}</div>
        </div>
        <div className={clsx(classes.replenishmentCell, classes.long)}>
          <div className={classes.rowTitle}>{`Quantity Received (${
            activeRequest?.assetUnit || ''
          })`}</div>
          <div>{replenishment?.quantityReceived}</div>
        </div>
        <div className={classes.replenishmentCell}>
          <div className={classes.rowTitle}>Unit Cost</div>
          <div>{numberToLocaleString(replenishment?.unitCost)}</div>
        </div>
        <div className={classes.replenishmentCell}>
          <div className={classes.rowTitle}>Total Cost</div>
          <div>{numberToLocaleString(replenishment?.totalCost)}</div>
        </div>
      </div>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Comments</div>
        <div>{replenishment?.comments}</div>
      </div>
      <div className={classes.createdSubtext}>{createdByText}</div>
      {showUpdatedByText && (
        <div className={classes.updatedSubtext}>{updatedByText}</div>
      )}
    </div>
  );
};

export default ReplenishmentView;
